import React from "react";
import {
  Button,
  Row,
  Col,
  Tooltip,
  Switch,
} from "antd";
import {
  UpSquareFilled,
  DownSquareFilled,
} from "@ant-design/icons";
import { isErgEffectivelyOn, normalizeStepMode, STEP_MODE } from "../utils/ergMode";

const MODE_LABEL = {
  free: "free ride",
  max: "best effort",
  manual: "manual",
};

const FTPControls = ({
  isMobile = false,
  FTP,
  additionalPower,
  onAdjustFTP,
  gradientMode = null,
  gradient = 0,
  canSimulateFlat = false,
  onAdjustGradient,
  ergEnabled = true,
  onToggleErg,
  pause = true,
  showErgToggle = false,
  controlHeight = 64,
  controlHeightMobile = 52,
  noTrainer = false,
}) => {
  const height = isMobile ? controlHeightMobile : controlHeight;

  // Four presentations, in priority order:
  //   no trainer connected     -> a plain badge: nothing is being measured and
  //                               nothing can be driven, so there is no number
  //                               to show and nothing to adjust
  //   ERG driving              -> the watts target, as before
  //   off ERG, can simulate    -> the gradient, which is both the reading and
  //                               the control
  //   off ERG, cannot simulate -> a plain label: this trainer refused Indoor
  //                               Bike Simulation, so there is no gradient and
  //                               nothing to adjust
  const showGradient = !noTrainer && Boolean(gradientMode) && canSimulateFlat;
  const showPlainManual = !noTrainer && Boolean(gradientMode) && !canSimulateFlat;

  const subLabel = showGradient ? MODE_LABEL[gradientMode] : null;
  const decrementLabel = showGradient ? "Decrease gradient" : "Decrease target power";
  const incrementLabel = showGradient ? "Increase gradient" : "Increase target power";
  const onDecrement = () => (showGradient ? onAdjustGradient(-1) : onAdjustFTP(-2));
  const onIncrement = () => (showGradient ? onAdjustGradient(1) : onAdjustFTP(2));

  // Where the ERG switch goes depends on the room available.
  //
  // Desktop: on the sub-label row inside the display box, so the control block
  // keeps the height it had before the switch existed.
  // Mobile: the box drops to 52px with a 0.75em font, which leaves no room for
  // a switch beside the label — it gets its own bar under the whole block.
  //
  // stepMode: gradientMode carries "free" | "max" | "manual" | null. Only the
  // first two are real step modes; "manual" and null both mean "an ordinary ERG
  // step", which is what decides whether the rider is allowed to touch this.
  const ergStepMode =
    gradientMode === "free" || gradientMode === "max" ? gradientMode : "erg";
  const ergIsOn = isErgEffectivelyOn({ ergEnabled, stepMode: ergStepMode });
  const ergPlanDriven = normalizeStepMode(ergStepMode) !== STEP_MODE.ERG;

  const ergSwitchEl = (
    <Switch
      size="small"
      checked={ergIsOn}
      disabled={!pause || ergPlanDriven}
      onChange={(next) => onToggleErg && onToggleErg(next)}
      aria-label="ERG control"
      title={
        ergPlanDriven
          ? "This step has no target to hold"
          : pause
            ? "Turn ERG control on or off"
            : "Pause first to change ERG control"
      }
    />
  );

  // Desktop only. The sub-label already names the mode right beside it, so the
  // word "ERG" is enough here.
  const inlineErgSwitch =
    showErgToggle && !isMobile ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        ERG
        {ergSwitchEl}
      </span>
    ) : null;

  // Mobile only. Standing on its own line it needs the full label back.
  const ergBar =
    showErgToggle && isMobile ? (
      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px dotted rgba(15, 23, 42, 0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 13, opacity: 0.85 }}>ERG control</span>
        {ergSwitchEl}
      </div>
    ) : null;

  // One shared sub-row so the three presentations below stay identical in shape.
  const subRow = (text) => (
    <div
      style={{
        fontSize: 12,
        fontWeight: 500,
        opacity: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        width: "100%",
        paddingLeft: 8,
        paddingRight: 8,
      }}
    >
      <span>{text}</span>
      {inlineErgSwitch}
    </div>
  );

  return (
    <div style={{ width: "100%" }}>
      <Row gutter={isMobile ? 8 : 6} align="middle" wrap={false} style={{ width: "100%" }}>
        {/* Left arrow beside target */}
        <Col flex={`${height}px`}>
          <Tooltip title={decrementLabel}>
            <Button
              id="tour-ftp-down"
              shape="circle"
              className="primary-glass-btn"
              onClick={onDecrement}
              disabled={showPlainManual || noTrainer}
              aria-label={decrementLabel}
              style={{ width: height, height }}
            >
              <DownSquareFilled />
            </Button>
          </Tooltip>
        </Col>

        {/* Target rectangle fills remaining width */}
        <Col flex="auto" style={{ margin: isMobile ? "0 8px" : "0 6px" }}>
          <div
            style={{
              textAlign: "center",
              height,
              borderRadius: 12,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0.25))",
              border: "1px solid rgba(0,0,0,0.18)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.35), 0 2px 10px rgba(0,0,0,0.08)",
              color: "rgba(0,0,0,0.78)",
              fontWeight: 600,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: isMobile ? "0.75em" : undefined,
            }}
            aria-label={
              noTrainer
                ? "No trainer connected"
                : showGradient
                  ? `Simulated gradient, ${gradient} percent`
                  : showPlainManual
                    ? "Manual control, no gradient available"
                    : "Target power display"
            }
          >
            {noTrainer ? (
              <>
                NO TRAINER
                {subRow("no trainer connected")}
              </>
            ) : showGradient ? (
              <>
                Gradient {gradient} %
                {subRow(subLabel)}
              </>
            ) : showPlainManual ? (
              <>
                Manual
                {subRow("no road simulation")}
              </>
            ) : (
              <>
                Target {FTP + additionalPower} W
                {subRow(`FTP ${FTP} W`)}
              </>
            )}
          </div>
        </Col>

        {/* Right arrow beside target */}
        <Col flex={`${height}px`}>
          <Tooltip title={incrementLabel}>
            <Button
              id="tour-ftp-up"
              shape="circle"
              className="primary-glass-btn"
              onClick={onIncrement}
              disabled={showPlainManual || noTrainer}
              aria-label={incrementLabel}
              style={{ width: height, height }}
            >
              <UpSquareFilled />
            </Button>
          </Tooltip>
        </Col>
      </Row>
      {ergBar}
    </div>
  );
};

export default FTPControls;
